import {Wave} from "react-animated-text";
import React, {useEffect, useRef, useState} from "react";

const SCENE_BG = {
    backgroundImage: 'linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url(/text-bg.webp)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
};

export const TextScene = ({isControl, text, lines, textStyle}) => {
    if(isControl) {
        return <div style={{
            ...textStyle,
            textAlign: 'center',
            width: '1500px',
            height: '900px',
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            justifyContent: 'center',
            ...SCENE_BG,
        }}>
            <div style={{fontSize: '160px'}}>{text ? text : lines.map((line) => <div>{line}</div>)}</div>
        </div>
    }

    return <div style={{
        flex: 1,
        textAlign: 'center',
        width: '100%',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...SCENE_BG,
    }}>
        <FullWidthText lines={text ? [text] : lines} textStyle={textStyle} maxFontSize={70} />
    </div>
}

// Generic instructions/punchline text scene styled like
// ChaosCharactersInstructions: tracked uppercase body paragraphs, optional
// italic colored punchline. Uses the same text-bg.webp backdrop as TextScene.
export const InstructionsScene = ({isControl, lines = [], punchline, accentColor = '#e6299e'}) => {

    const paragraphStyle = {
        fontSize: isControl ? 'clamp(32px, 4vw, 60px)' : 'clamp(22px, 3vw, 44px)',
        fontWeight: 800,
        lineHeight: 1.3,
        letterSpacing: 4,
        textTransform: 'uppercase',
        textShadow: '2px 2px 0 rgba(0,0,0,0.95)',
        marginBottom: 32,
    };
    const punchlineStyle = {
        marginTop: 48,
        fontSize: isControl ? 'clamp(48px, 6vw, 96px)' : 'clamp(32px, 4.5vw, 72px)',
        fontWeight: 900,
        fontStyle: 'italic',
        letterSpacing: 8,
        textTransform: 'uppercase',
        color: accentColor,
        textShadow: `0 0 18px ${accentColor}aa, 2px 2px 0 rgba(0,0,0,0.95)`,
    };
    const wrapperStyle = isControl ? {
        textAlign: 'center',
        width: '1500px',
        height: '900px',
        alignSelf: 'flex-start',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 60px',
        boxSizing: 'border-box',
        color: '#fff',
        ...SCENE_BG,
    } : {
        width: '100%',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px 32px',
        boxSizing: 'border-box',
        color: '#fff',
        ...SCENE_BG,
    };
    return <div style={wrapperStyle}>
        <div style={{maxWidth: isControl ? 1200 : 900}}>
            {lines.map((line, i) => <p key={i} style={paragraphStyle}>{line}</p>)}
            {punchline && <p style={punchlineStyle}>{punchline}</p>}
        </div>
    </div>;
};

export const GetReady = ({isControl}) => {
    return <TextScene isControl={isControl} text={<Wave text="Get Ready..." effect="color" effectChange={'#262626'}/>}/>;
}

const FullWidthText = ({ lines = [], maxFontSize = 500, textStyle = {} }) => {
    const containerRef = useRef(null);
    const lineRefs = useRef([]);
    const [fontSizes, setFontSizes] = useState([]);

    const calculateFontSize = (textRef, containerWidth) => {
        if (!textRef) return 0;

        let low = 1;
        let high = maxFontSize;

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            textRef.style.fontSize = `${mid}px`;

            if (textRef.offsetWidth <= containerWidth) {
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }

        return Math.min(high, maxFontSize);
    };

    // String key for content-based comparison (lines/textStyle are unstable
    // refs every render — using them as deps caused an infinite loop because
    // setFontSizes rerenders, parent recreates the lines array, deps "change",
    // effect re-fires, repeat).
    const linesKey = lines.map(l => typeof l === 'string' ? l : '~jsx~').join('|');

    useEffect(() => {
        lineRefs.current = lineRefs.current.slice(0, lines.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linesKey]);

    useEffect(() => {
        const resizeText = () => {
            if (!containerRef.current) return;

            const containerWidth = containerRef.current.offsetWidth;

            const newFontSizes = lineRefs.current.map(ref =>
                calculateFontSize(ref, containerWidth)
            );

            setFontSizes(prev => {
                if (prev.length === newFontSizes.length &&
                    prev.every((v, i) => v === newFontSizes[i])) return prev;
                return newFontSizes;
            });
        };

        resizeText();
        window.addEventListener('resize', resizeText);

        return () => {
            window.removeEventListener('resize', resizeText);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linesKey, maxFontSize]);

    const combinedTextStyle = (fontSize) => ({
        ...textStyle,
        fontSize: `${fontSize}px`,
        whiteSpace: 'nowrap',
    });

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {lines.map((line, index) => (
                <div
                    key={index}
                    ref={el => lineRefs.current[index] = el}
                    style={combinedTextStyle(fontSizes[index] || 100)}
                >
                    {line}
                </div>
            ))}
        </div>
    );
};
